import React, { useState } from 'react';
import { useQuran } from '../context/QuranContext';
import { ChevronLeft, ChevronRight, X, BookOpen, Sparkles, Bookmark } from 'lucide-react';
import { Button } from './ui/button';

const RootNavigator = () => {
    const { 
        rootBrowsingContext, 
        goToNextRootVerse, 
        goToPreviousRootVerse,
        goToRootVerse,
        clearRootBrowsing,
        surahs
    } = useQuran();

    const [isCommentExpanded, setIsCommentExpanded] = useState(false);

    if (!rootBrowsingContext || rootBrowsingContext.verses.length === 0) {
        return null;
    }

    const { word, verses, currentIndex, browseType } = rootBrowsingContext;
    const rootText = word?.text_uthmani || word?.text_indopak || word?.text || '';
    const currentVerseKey = verses[currentIndex]?.verse_key || '';
    
    const isAISearch = browseType === 'search';
    const isTopic = browseType === 'topic';
    const currentReason = isAISearch ? (verses[currentIndex]?.reason || '') : '';
    const currentTopicComment = isTopic ? (verses[currentIndex]?.topic_comment || '') : '';
    
    const getSurahName = (verseKey) => {
        const surahId = parseInt(verseKey.split(':')[0]);
        const surah = surahs.find(s => s.id === surahId);
        return surah ? surah.name_simple : '';
    };

    const isFirst = currentIndex === 0;
    const isLast = currentIndex === verses.length - 1;

    return (
        <div 
            className={`fixed bottom-0 left-0 right-0 z-50 backdrop-blur-lg border-t px-2 py-2 sm:px-4 sm:py-2 ${
                isAISearch 
                    ? 'bg-[#09090b]/95 border-[#8b5cf6]/30' 
                    : isTopic
                        ? 'bg-[#09090b]/95 border-[#10b981]/30'
                        : 'bg-[#09090b]/95 border-[#d4af37]/30'
            }`}
            data-testid="root-navigator"
        >
            <div className="max-w-5xl mx-auto">
                {/* Header row */}
                <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <div className="flex items-center gap-2">
                        {isAISearch ? (
                            <>
                                <Sparkles className="w-4 h-4 text-[#8b5cf6]" />
                                <span className="text-[#8b5cf6] font-medium text-sm">AI Search Results</span>
                            </>
                        ) : isTopic ? (
                            <>
                                <Bookmark className="w-4 h-4 text-[#10b981]" />
                                <span className="text-[#10b981] font-medium text-sm">Topic Reading</span>
                            </>
                        ) : (
                            <>
                                <BookOpen className="w-4 h-4 text-[#d4af37]" />
                                <span className="text-[#d4af37] font-medium text-sm">Root Word Browser</span>
                            </>
                        )}
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearRootBrowsing}
                        className="text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#1e293b] h-6 px-2 text-xs"
                        data-testid="close-root-nav-btn"
                    >
                        <X className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Exit</span>
                    </Button>
                </div>

                {/* Navigation row */}
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                    {/* Previous */}
                    <Button
                        onClick={goToPreviousRootVerse}
                        disabled={isFirst}
                        className="flex-shrink-0 bg-[#1e293b] hover:bg-[#334155] text-[#f8fafc] border border-[#334155] disabled:opacity-40 h-8 px-2 sm:px-3 text-xs sm:text-sm"
                        data-testid="prev-root-verse-btn"
                    >
                        <ChevronLeft className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Previous</span>
                    </Button>

                    {/* Center info */}
                    <div 
                        className={`flex-1 text-center min-w-0 ${(currentTopicComment || currentReason) ? 'cursor-pointer select-none' : ''}`}
                        onClick={() => {
                            if (currentTopicComment || currentReason) {
                                setIsCommentExpanded(!isCommentExpanded);
                            }
                        }}
                    >
                        <div className="flex items-center justify-center gap-2 text-sm leading-tight">
                            {isAISearch ? (
                                <span className="text-[#8b5cf6] text-sm truncate max-w-[200px]">"{rootText}"</span>
                            ) : isTopic ? (
                                <span className="text-[#10b981] text-sm truncate max-w-[200px]">{rootText}</span>
                            ) : (
                                <span className="arabic-text text-[#d4af37] text-base" dir="rtl">{rootText}</span>
                            )}
                        </div>
                        <p className="text-[#94a3b8] text-[10px] sm:text-xs mt-0.5 truncate">
                            <span className="text-[#f8fafc]">{currentIndex + 1}</span> of {verses.length} verses
                            {currentVerseKey && (
                                <span className={`ml-1 sm:ml-2 ${isAISearch ? 'text-[#8b5cf6]' : isTopic ? 'text-[#10b981]' : 'text-[#d4af37]'}`}>
                                    • {getSurahName(currentVerseKey)} {currentVerseKey}
                                </span>
                            )}
                        </p>
                        {currentReason && (
                            <div className="mt-0.5">
                                {isCommentExpanded ? (
                                    <p className="text-[#a78bfa] text-[11px] sm:text-xs italic leading-snug whitespace-pre-wrap text-left p-1.5 sm:p-2 bg-[#8b5cf6]/5 rounded border border-[#8b5cf6]/20">
                                        Match: {currentReason}
                                    </p>
                                ) : (
                                    <p className="text-[#a78bfa] text-[11px] sm:text-xs italic line-clamp-1 sm:line-clamp-2 leading-snug px-2">
                                        Match: {currentReason}
                                    </p>
                                )}
                            </div>
                        )}
                        {currentTopicComment && (
                            <div className="mt-0.5">
                                {isCommentExpanded ? (
                                    <pre className="text-[#34d399] text-[11px] sm:text-xs italic leading-snug whitespace-pre-wrap font-sans text-left bg-[#10b981]/5 p-1.5 sm:p-2 rounded border border-[#10b981]/20">
                                        {currentTopicComment}
                                    </pre>
                                ) : (
                                    <p className="text-[#34d399] text-[11px] sm:text-xs italic line-clamp-1 sm:line-clamp-2 leading-snug px-2">
                                        Note: {currentTopicComment}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Next */}
                    <Button
                        onClick={goToNextRootVerse}
                        disabled={isLast}
                        className={`flex-shrink-0 font-semibold disabled:opacity-40 h-8 px-2 sm:px-3 text-xs sm:text-sm ${
                            isAISearch 
                                ? 'bg-[#8b5cf6] hover:bg-[#a78bfa] text-white' 
                                : isTopic
                                    ? 'bg-[#10b981] hover:bg-[#059669] text-white'
                                    : 'bg-[#d4af37] hover:bg-[#facc15] text-[#02040a]'
                        }`}
                        data-testid="next-root-verse-btn"
                    >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight className="w-4 h-4 sm:ml-1" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default RootNavigator;
